import { useState } from 'react';
import { convertToOptions } from 'lib/helpers';

import { selectCurrentUser } from 'features/auth/slice';
import { selectTags } from 'features/tags/slice';
import { useSelector } from 'react-redux';

import {
  Card,
  FieldWrapper,
  Heading,
  HelperText,
  Icon,
  InputWrapper,
  Label,
  SelectInput,
  TextInput,
  ToggleInput,
  useModal,
} from '@frontendmentorio/design-system-v2';

import Modal from './Modal';
import ProFlag from 'features/shared/ProFlag';

const VALID_LIVE_URLS = [
  /.*\.deno\.dev/,
  /.*\.editorx\.io/,
  /.*\.firebaseapp\.com/,
  /.*\.fly\.dev/,
  /.*\.github\.io/,
  /.*\.gitlab\.io/,
  /.*\.herokuapp\.com/,
  /.*\.netlify\.app/,
  /.*\.onrender\.com/,
  /.*\.pages\.dev/,
  /.*\.repl\.co/,
  /.*\.surge\.sh/,
  /.*\.vercel\.app/,
  /.*\.web\.app/,
  /.*\.azurestaticapps\.net/,
  /.*\.tiiny\.site/,
];

const VALID_REPO_URLS = [
  /bitbucket\.com\/.+\/.+/,
  /bitbucket\.org\/.+\/.+/,
  /github\.com\/.+\/.+/,
  /gitlab\.com\/.+\/.+/,
];

export default function GeneralInfo({ solution }) {
  const modal = useModal();

  const currentUser = useSelector(selectCurrentUser);
  const tags = useSelector(selectTags);
  const options = convertToOptions(tags);

  const [isPrivate, setIsPrivate] = useState(false);

  return (
    <Card
      aria-labelledby="general-info-heading"
      className="flex flex-col gap-5 p-5 z-10"
      role="group"
    >
      <Heading id="general-info-heading" level="5" tagName="span">
        General information
      </Heading>

      <FieldWrapper>
        <Label isRequired>Solution title</Label>

        <HelperText>
          Include some of the tools and techniques you used to complete the
          challenge
        </HelperText>

        <InputWrapper
          defaultValue={solution?.title}
          input={TextInput}
          name="title"
          placeholder="e.g. Responsive landing page using CSS Grid"
          validations={{ required: true, maxLength: 70 }}
        />
      </FieldWrapper>

      <FieldWrapper>
        <Label isRequired>Repository URL</Label>

        <HelperText>
          Read our{' '}
          <a
            className="underline hover:no-underline"
            href="https://medium.com/frontend-mentor/a-complete-guide-to-submitting-solutions-on-frontend-mentor-ac6384162248"
          >
            complete guide to submitting solutions
          </a>{' '}
          for help creating a repository
        </HelperText>

        <InputWrapper
          defaultValue={solution?.repoURL}
          input={TextInput}
          name="repoURL"
          type="url"
          validations={{
            required: true,
            url: true,
            validate: {
              notSettings: (value) => {
                if (value.includes('settings')) {
                  return 'Please remove "/settings" from the end of the URL';
                }
              },
              allowedDomains: (value) => {
                if (!VALID_REPO_URLS.some((pattern) => pattern.test(value))) {
                  return 'This should be a GitHub, GitLab or BitBucket repository';
                }
              },
            },
          }}
        />
      </FieldWrapper>

      <FieldWrapper>
        <Label isRequired>Live site URL</Label>

        <HelperText>
          Read more about our{' '}
          <a
            className="underline hover:no-underline"
            href="https://medium.com/frontend-mentor/frontend-mentor-trusted-hosting-providers-bf000dfebe"
          >
            recommended free hosting options
          </a>{' '}
          for help creating a repository
        </HelperText>

        <InputWrapper
          defaultValue={solution?.liveURL}
          input={TextInput}
          name="liveURL"
          type="url"
          validations={{
            required: true,
            url: true,
            validate: {
              isLive: (value) => {
                if (VALID_REPO_URLS.some((pattern) => pattern.test(value))) {
                  return 'This should be a live version of your site, not the repository';
                }
              },
              allowedDomains: (value) => {
                if (currentUser?.hasActiveSubscription) return;

                if (!VALID_LIVE_URLS.some((pattern) => pattern.test(value))) {
                  return 'Invalid domain. Please check README for recommended hosts';
                }
              },
            },
          }}
        />
      </FieldWrapper>

      <FieldWrapper>
        <Label>
          Which tools, libraries, frameworks, or methodologies did you use for
          this project?
        </Label>

        <HelperText>
          Add up to 5 tags for your project’s tools and approaches, excluding
          HTML, CSS, and JavaScript. Missing a tag? Request it at{' '}
          <a
            className="underline hover:no-underline"
            href="mailto:hi@frontendmentor.io"
          >
            hi@frontendmentor.io
          </a>
        </HelperText>

        <InputWrapper
          defaultValue={solution?.tags.map((tag) => tag.id)}
          input={SelectInput}
          isMulti
          name="tags"
          options={options}
          validations={{ maxOptions: 5 }}
        />
      </FieldWrapper>

      <div className="bg-gray-100 flex flex-wrap gap-3 items-center justify-center p-5 rounded-lg">
        <InputWrapper
          ariaLabel={`Make your solution private, currently set to ${
            isPrivate ? 'private' : 'public'
          }`}
          defaultChecked={solution?.private}
          disabled={!currentUser?.hasActiveSubscription}
          offLabel="Public"
          onChange={(value) => setIsPrivate(value)}
          onLabel="Private"
          name="private"
          input={ToggleInput}
        />

        <ProFlag />

        <button
          aria-label="Learn more about private solutions"
          onClick={modal.open}
          type="button"
        >
          <Icon className="text-cyan-500" name="circle-info" style="solid" />
        </button>
      </div>

      <Modal modal={modal} />
    </Card>
  );
}
